/**
 * M14-2 — the session stream. Replaces the four-second poll both session screens ran.
 *
 * **The credential travels in the subprotocol.** A browser cannot set headers on a
 * WebSocket, so it goes as the second `Sec-WebSocket-Protocol` value and the server echoes
 * only the first. A query string would put the auditee's access token — which *is* the whole
 * authorisation — into every access log, proxy log and `Referer`, which is exactly what the
 * portal link already avoids by living in a fragment.
 *
 * **The server decides what a viewer may see, always.** Every frame is a whole projection
 * built in the watcher's own database transaction, so this file never filters and never
 * merges: it replaces. A client that patched frames together would be a second opinion about
 * what an auditee may read.
 *
 * **Reconnection is expected, not exceptional.** A laptop lid closes, a train enters a
 * tunnel, a proxy times an idle socket out. Backoff is capped low because the thing on the
 * other end is a live audit and a minute of staleness is a minute of an auditor talking to a
 * screen that stopped moving.
 */

/** Named to match the server. Changing one without the other closes every connection. */
const SUBPROTOCOL = "complao.v1";

const FIRST_RETRY_MS = 500;
const MAX_RETRY_MS = 8_000;

export interface StreamHandle {
  /** Stop reconnecting and close. Idempotent. */
  close(): void;
}

interface Options<T> {
  /** Path from the API root, e.g. `/audits/{id}/session/stream` or `/portal/session/stream`. */
  path: string;
  /** The bearer token for staff, or the auditee's access token. */
  token: string;
  onState(state: T): void;
  /** Called with `true` on open and `false` on drop, so a screen can say it is reconnecting. */
  onConnected?(connected: boolean): void;
}

function socketUrl(path: string): string {
  // The same prefix client.ts uses. Written once there and mirrored here rather
  // than imported, because that module owns the bearer header and this one must not.
  const url = new URL("/api/v1" + path, location.origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

/**
 * Open the stream and keep it open. Returns a handle whose `close` ends it for good.
 *
 * Reconnection carries no cursor. The server sends a whole projection, so a frame is the
 * replay from any point — and asking for a delta would mean this file holding a transcript
 * the server did not send it, which is the merging the design avoids.
 */
export function openStream<T>({ path, token, onState, onConnected }: Options<T>): StreamHandle {
  let socket: WebSocket | null = null;
  let timer: number | undefined;
  let retry = FIRST_RETRY_MS;
  let closed = false;

  const connect = () => {
    if (closed) return;
    // Two values: the protocol the server must echo, then the credential it must not.
    socket = new WebSocket(socketUrl(path), [SUBPROTOCOL, token]);

    socket.onopen = () => {
      retry = FIRST_RETRY_MS;
      onConnected?.(true);
    };

    socket.onmessage = (event) => {
      try {
        onState(JSON.parse(String(event.data)) as T);
      } catch {
        // A frame this client cannot parse is a frame it must not act on. Dropping it is
        // safe: the next change sends the whole state again, so nothing is lost but a tick.
      }
    };

    socket.onclose = () => {
      onConnected?.(false);
      if (closed) return;
      // A close with no retry is a screen that silently stops updating mid-audit — the one
      // failure mode worse than polling, because polling at least kept trying.
      timer = window.setTimeout(connect, retry);
      retry = Math.min(retry * 2, MAX_RETRY_MS);
    };

    // `onerror` always precedes `onclose`; letting close alone drive retry keeps one path.
    socket.onerror = () => undefined;
  };

  connect();

  return {
    close() {
      closed = true;
      window.clearTimeout(timer);
      const going = socket;
      socket = null;
      if (!going) return;
      // `close()` on a socket still in CONNECTING is legal and aborts the handshake, but
      // every browser logs it as *"WebSocket is closed before the connection is
      // established"* — and React's StrictMode mounts, unmounts and remounts every effect
      // in development, so the first socket of every screen is always closed mid-handshake.
      // Deferring to `onopen` closes it cleanly instead, which keeps a real error visible
      // in a console that would otherwise cry wolf on every page load.
      //
      // The abandoned socket opens for an instant and goes. That costs nothing: a dropped
      // connection does not end the session, which the server guarantees anyway.
      if (going.readyState === WebSocket.CONNECTING) {
        going.onopen = () => going.close();
        going.onmessage = null;
        going.onclose = null;
      } else {
        going.close();
      }
    },
  };
}
